import React from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Form, Modal, Row } from 'react-bootstrap';
import {
    Button,
    Switch,
    FormControlLabel
} from '@sumologic/yama-ds-components';

import {
    isMd5,
    isSha1,
    isSha256,
    isValidUrl,
    isIpv4Address,
    isEmailAddress
} from '~/modules/validation';

import {
    generateFieldName,
    getArtifactsValidationSchema
} from '~/modules/getArtifactsValidationSchema';

import { toErrorMessage } from '~/modules/helpers/error';

import File from '~/models/File';
import Artifact from '~/models/Artifact';
import ObjectModel, { objectTypes } from '~/models/Object';

import Tag from '~/components/Tags/Tags';
import Select from '~/components/Select/Select';
import Editor2 from '~/components/Editor2/Editor2';
import Translate from '~/components/Translate/Translate';
import HeaderModal from '~/components/HeaderModal/HeaderModal';
import PageOverlay from '~/components/PageOverlay/PageOverlay';
import FileUploader from '~/components/FileUploader/FileUploader';

import IncidentArtifactCreationTable from './IncidentArtifactCreationTable';

import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';

// FM: split tags for every comma or tab
const splitTagsRE = /([^\n\t\r,])+/g;

// Get a comma separated string of mapped values:
const toCsmv = arr => arr.map(e => e.value).join();

const keysLen = e => (e ? Object.keys(e).length : 0);

class AddObjectModal extends React.Component {
    static contextType = StickyAlertContext;
    constructor(props) {
        super(props);
        const initialData = this.props.initialData || {};
        this.state = {
            objValue: initialData.objValue || [],
            objectTagValues: [],
            fileToUpload: [],
            artifactItems: [],
            artifactValidationSchema: {},
            objFileData: initialData.objFileData || {},
            objType: initialData.objType || {},
            additionalInfo: '',
            selectedHashType: initialData.selectedHashType || {},
            autoLock: false,
            isValidForm: initialData.isValidForm ?? false,
            createNewFile: false,
            showPageOverlay: false,
            isMounted: false
        };
        this.filesForms = [
            {
                id: 'name',
                label: 'File Name',
                type: 'text',
                required: true
            },
            {
                id: 'hash',
                label: 'Select hash',
                type: 'hash',
                option: [
                    {
                        label: 'Hash MD5',
                        value: 'md5'
                    },
                    {
                        label: 'Hash SHA1',
                        value: 'sha1'
                    },
                    {
                        label: 'Hash SHA256',
                        value: 'sha256'
                    }
                ]
            }
        ];
    }

    componentWillUnmount() {
        this.state.isMounted = false;
    }

    componentDidMount() {
        this.state.isMounted = true;
    }

    hidePageOverlay = () => {
        if (this.state.isMounted) {
            this.setState({
                showPageOverlay: false
            });
        }
    };

    /*
    // currently not used:
    hasValidHashes= () => {
        return Object.values(this.state.isValidForm).some(value => true === value);
    };
    */

    successCallback = data => {
        this.props.handleUpdateData?.(data);
        this.props.onHide();
    };

    alertCreateObjectError = message => {
        this.context.notifyError({
            title: <Translate message="Create object" />,
            message: message || (
                <Translate message="Error while creating the object" />
            )
        });
    };

    alertCreateObjectSuccess = message => {
        this.context.notifySuccess({
            title: <Translate message="Create object" />,
            message: message || <Translate message="Object created" />
        });
    };

    _createArtifact = () => {
        const artifact = new Artifact(null, this.props.incidentId);
        artifact.items = this.state.artifactItems.map(e =>
            artifact.createItem(e.keyName, e.value)
        );
        artifact.tag = toCsmv(this.state.objectTagValues);
        artifact
            .save()
            .then(response => {
                if (200 !== response.status) {
                    throw response;
                } else {
                    const data = response.data;
                    if (data) {
                        data.savedArtifact = artifact;
                    }
                    this.successCallback(data);
                }
            })
            .catch(exception => {
                if (APP_INFO.debug) {
                    console.debug(exception);
                }
                this.alertCreateObjectError(toErrorMessage(exception));
            })
            .then(this.hidePageOverlay);
    };

    _addArtifactItem = item => {
        // todo: check if also needs validation like remove / upddate
        this.setState({
            artifactItems: [...this.state.artifactItems, item]
        });
    };

    _removeArtifactItem = index => {
        const items = [...this.state.artifactItems];
        items.splice(index, 1);
        this.setState({
            artifactItems: items
        });
    };

    _updateArtifactItem = (index, item) => {
        const items = [...this.state.artifactItems];
        items[index] = { ...items[index], ...item };
        this.setState({
            artifactItems: items
        });
    };

    _validateFormArtifacts = () => {
        this.validateForm('incidentArtifact', this.state.artifactItems, true);
    };

    splitTags = tags => {
        if (tags) {
            const re = splitTagsRE;
            const formattedTags = [];
            for (const tag of tags) {
                const matches = tag.value?.match(re);
                if (matches) {
                    //FM: add tag to new structure compliant to tag component
                    for (const e of matches) {
                        const str = e.trim();
                        formattedTags.push({
                            value: str,
                            label: str,
                            deletable: true
                        });
                    }
                }
            }
            return formattedTags;
        }
    };

    createObjTypeListData = () => {
        const list = [];
        for (const objectType of objectTypes) {
            const value = objectType.dashed;
            if ('hash' !== value) {
                list.push({
                    label: objectType.human,
                    value: value
                });
            }
        }
        if (this.props.incidentId) {
            list.push({
                label: 'Artifact',
                value: 'incident-artifact'
            });
        }
        return list;
    };

    setAdditionalInfoData = (event, editor) => {
        this.setState({
            additionalInfo: editor.getData()
        });
    };

    setFormValid = (type, bool) => {
        if (this.state.isMounted) {
            this.setState(prevState => {
                return {
                    isValidForm: {
                        ...prevState.isValidForm,
                        [type]: bool
                    }
                };
            });
        }
    };

    validateForm = (type, value, required = false) => {
        let validForm;
        if (value?.length) {
            switch (type) {
                case 'md5':
                    validForm = isMd5(value);
                    break;
                case 'sha1':
                    validForm = isSha1(value);
                    break;
                case 'sha256':
                    validForm = isSha256(value);
                    break;
                case 'mail-addresses':
                    validForm = isEmailAddress(value);
                    break;
                case 'ip-addresses':
                    validForm = isIpv4Address(value);
                    break;
                case 'url-addresses':
                case 'domain':
                    validForm = isValidUrl(value);
                    break;
                case 'incidentArtifact': {
                    const values = value.reduce((acc, item, index) => {
                        const itemKey = generateFieldName(0, index);
                        acc[itemKey] = item.value;
                        return acc;
                    }, {});
                    this.state.artifactValidationSchema
                        .validate(values)
                        .then(() => {
                            this.setFormValid(type, true);
                        })
                        .catch(exception => {
                            if (APP_INFO.debug) {
                                console.debug(exception);
                            }
                            this.setFormValid(type, false);
                        });
                    break;
                }
                // case 'user-details':
                default:
                    validForm = true;
            }
        } else {
            validForm = !required;
        }
        this.setFormValid(type, validForm);
        return validForm;
    };

    handleFiles = file => {
        const allFiles = [...file, ...this.state.fileToUpload];
        //RN: limit the upload to 10 files max according to @MBacaloni
        if (
            allFiles &&
            allFiles?.length <= 10 // <<
        ) {
            this.setState({
                fileToUpload: allFiles
            });
        } else {
            this.alertCreateObjectError(
                <Translate message="You can only upload 10 files at a time" />
            );
        }
    };

    handleRemoveFile = index => {
        const updatedFiles = [...this.state.fileToUpload];
        updatedFiles?.splice(index, 1);
        this.setState({
            fileToUpload: updatedFiles
        });
    };

    notValidForm = key => {
        const e = this.state.isValidForm;
        return e.hasOwnProperty(key) && !e[key];
    };

    inputField = (element, type = null) => {
        let disabled = !!this.props.initialData;
        if (!type) {
            type = element.type;
            disabled = false;
        }
        if (this.state.editDuplicatedFiles) {
            disabled = true;
        }
        let id = element.id;
        if ('hash' === id) {
            id = this.state?.selectedHashType?.value + '_hash';
        }

        return (
            <Translate message="Insert value">
                {insertValue => (
                    <Form.Control
                        placeholder={insertValue}
                        id={id} // react-bootstrap.github.io/components/forms/#form-control-props
                        isInvalid={this.notValidForm(type)}
                        onChange={event => {
                            const { value } = event.target;
                            const { objFileData, isValidForm } = this.state;
                            if (value.length) {
                                this.validateForm(
                                    type,
                                    value,
                                    element.required
                                );
                                objFileData[id] = value;
                            } else {
                                delete objFileData[id];
                                delete isValidForm[type];
                            }
                            this.setState({
                                objFileData: objFileData
                            });
                        }}
                        disabled={disabled}
                        value={this.state.objFileData[id] || ''}
                    />
                )}
            </Translate>
        );
    };

    manageApplyButton = () => {
        //FM: return disabled button state
        const isValidForm = Object.values(this.state.isValidForm);
        if (isValidForm.length) {
            const allTrue = isValidForm.every(e => true === e);
            if ('artifacts' === this.state.objType.value) {
                return !(allTrue && this.state.objFileData.name?.length);
            } else {
                return !allTrue;
            }
        } else {
            return true;
        }
    };

    onApply = () => {
        const {
            isValidForm,
            objectTagValues,
            objFileData,
            objValue,
            objType,
            createNewFile,
            fileToUpload,
            autoLock,
            additionalInfo
        } = this.state;
        if (isValidForm) {
            this.setState({
                showPageOverlay: true
            });
            const done = this.hidePageOverlay;
            const incidentId = this.props.incidentId;
            const objTypeValue = objType.value;
            if ('artifacts' !== objTypeValue && keysLen(objType)) {
                if ('incident-artifact' === objTypeValue) {
                    return this._createArtifact();
                } else {
                    const params = {
                        id: '',
                        incident_id: incidentId,
                        additional_info: additionalInfo,
                        autoLock: autoLock,
                        type: objTypeValue,
                        tag: toCsmv(objectTagValues),
                        name: objValue
                            .map(
                                'url-addresses' !== objTypeValue
                                    ? e => e.label
                                    : e => {
                                          // remove trailing slash from url:
                                          // Q: if there is a reason to use .slice(-1) instead of .charAt(0) ?
                                          const str = e.label;
                                          return '/' !== str.slice(-1)
                                              ? str
                                              : str.slice(0, -1);
                                      }
                            )
                            .join()
                    };
                    ObjectModel.saveObservable(params)
                        .then(response => {
                            if (
                                200 !== response.status ||
                                !response.data?.created
                            ) {
                                throw response;
                            } else {
                                this.successCallback(response.data);
                            }
                        })
                        .catch(exception => {
                            if (APP_INFO.debug) {
                                console.debug(exception);
                            }
                            let error = '';
                            const detail = exception.response?.data?.detail;
                            if (!detail) {
                                const paramName = params.name;
                                switch (params.type) {
                                    case 'ip-addresses':
                                        error = `An IP address with the value ${paramName} already exists`;
                                        break;
                                    case 'url-addresses':
                                        error = `An URL with the value ${paramName} already exists`;
                                        break;
                                    case 'mail-addresses':
                                        error = `An email address with the value ${paramName} already exists`;
                                        break;
                                    case 'user-details':
                                        error = `An user with the value ${paramName} already exists`;
                                        break;
                                    case 'domain':
                                        error = `A domain with the value ${paramName} already exists`;
                                        break;
                                    default:
                                        error =
                                            'An error occurred during object creation';
                                }
                            }
                            this.alertCreateObjectError(
                                toErrorMessage(exception)
                            );
                        })
                        .then(done);
                }
            } else {
                const handleSavedFile = response => {
                    if (200 !== response.status) {
                        throw response;
                    } else {
                        this.successCallback({
                            created: true,
                            new_id: response.data
                        });
                    }
                };
                if (fileToUpload.length) {
                    let formData = new FormData();
                    formData.append('additional_info', additionalInfo);
                    formData.append('force_duplicate_saving', createNewFile);
                    formData.append('auto_lock', autoLock);
                    formData.append('name', objFileData.name);
                    formData.append('tag', toCsmv(objectTagValues));
                    formData.append('remove_file', true);
                    for (const file of fileToUpload) {
                        formData.append('files', file);
                    }
                    File.saveWFile(incidentId, formData)
                        .then(handleSavedFile)
                        .catch(exception => {
                            if (APP_INFO.debug) {
                                console.debug(exception);
                            }
                            formData = new FormData(); // <<
                            const respData = exception?.response?.data;
                            const details = respData?.details;
                            if (details?.all_ok) {
                                const created_ids = details.created_ids;
                                const existing_ids = details.existing_ids;
                                this.alertCreateObjectError(
                                    <Translate message="Check duplicated files in modal" />
                                );
                                let existingFiles = [...fileToUpload]; // <<
                                existingFiles = existingFiles.filter(file => {
                                    return existing_ids.some(exFile => {
                                        const bool =
                                            exFile.uploaded_name === file.name;
                                        if (bool) {
                                            file.tooltip = (
                                                <>
                                                    <Translate message="Existing file name" />
                                                    :
                                                    <br />
                                                    {exFile.actual_name}
                                                </>
                                            );
                                        }
                                        return bool;
                                    });
                                });
                                this.setState({
                                    editDuplicatedFiles: true,
                                    fileToUpload: existingFiles
                                });
                                if (created_ids?.length) {
                                    this.alertCreateObjectSuccess(
                                        <Translate
                                            message="{num} files created"
                                            values={{ num: created_ids.length }}
                                        />
                                    );
                                }
                            } else {
                                this.alertCreateObjectError(
                                    toErrorMessage(exception)
                                );
                            }
                        })
                        .then(done);
                } else {
                    const params = {
                        incident_id: incidentId,
                        autoLock: autoLock,
                        fields: { artifacts: objFileData },
                        additional_info: additionalInfo,
                        tag: toCsmv(objectTagValues),
                        ButtonSaveAndContinue: 2,
                        force_duplicate_saving: createNewFile
                        // skip_field_hash_check: createNewFile === true ? 1 : 0,
                    };
                    File.save(params)
                        .then(handleSavedFile)
                        .catch(exception => {
                            if (APP_INFO.debug) {
                                console.debug(exception);
                            }
                            this.alertCreateObjectError(
                                toErrorMessage(exception)
                            );
                        })
                        .then(done);
                }
            }
        } else {
            return false;
        }
    };

    onUpdate = () => {
        const { objectTagValues, fileToUpload, autoLock, additionalInfo } =
            this.state;
        this.setState({
            showPageOverlay: true
        });
        const done = this.hidePageOverlay;
        const incidentId = this.props.incidentId;
        const handleSavedFile = response => {
            if (200 !== response.status) {
                throw response;
            } else {
                this.successCallback({
                    created: true,
                    new_id: response.data
                });
                this.alertCreateObjectSuccess(
                    <Translate
                        message="{num} files updated"
                        values={{ num: fileToUpload.length }}
                    />
                );
            }
        };
        let formData = new FormData();
        formData.append('additional_info', additionalInfo);
        formData.append('auto_lock', autoLock);
        formData.append('tag', toCsmv(objectTagValues));
        formData.append('remove_file', true);
        formData.append('overwrite', true);
        for (const file of fileToUpload) {
            formData.append('files', file);
        }
        File.saveWFile(incidentId, formData)
            .then(handleSavedFile)
            .catch(exception => {
                if (APP_INFO.debug) {
                    console.debug(exception);
                }
                this.alertCreateObjectError(toErrorMessage(exception));
            })
            .then(done);
    };

    renderEditor = () => {
        return (
            <Editor2
                collapsible
                buttonInHeader
                className="h-100"
                data={this.state.additionalInfo}
                onChange={this.setAdditionalInfoData}
                title={<Translate message="Description" />}
                config={{
                    images: {
                        enabled: true,
                        preventDuplicates: true
                    }
                }}
            />
        );
    };

    renderFiles = () => {
        //FM: render file form with hash fields, file uploader and add-info
        let titleForm = [this.filesForms[0]];
        if (!this.state.fileToUpload.length) {
            titleForm = this.filesForms;
        } else if (this.state.fileToUpload.length >= 1) {
            titleForm = [];
        }
        return (
            <>
                <Form.Group className="form-group">
                    {titleForm.map(element => {
                        // prettier-ignore
                        return (
                            <div className="mb-3" key={element.id}>
                                <Form.Label
                                    required={'name' === element.id}
                                    className="mb-1"
                                >
                                    {element.label}
                                </Form.Label>
                                {'hash' !== element.type ? (
                                    this.inputField(element)
                                ) : (
                                    <Row>
                                        <Col sm="3">
                                            <Select
                                                id="hashSelect"
                                                options={element.option}
                                                isDisabled={
                                                    !!this.props.initialData ||
                                                    this.state.editDuplicatedFiles
                                                }
                                                value={
                                                    this.state?.selectedHashType
                                                }
                                                onChange={option => {
                                                    const objFileData = this.state.objFileData;
                                                    delete objFileData[
                                                        this.state
                                                            ?.selectedHashType
                                                            ?.value + '_hash'
                                                    ];
                                                    const isValidForm = this.state.isValidForm;
                                                    delete isValidForm[
                                                        this.state
                                                            ?.selectedHashType
                                                            ?.value
                                                    ];
                                                    this.setState({
                                                        selectedHashType: option,
                                                        objFileData: objFileData,
                                                        isValidForm: isValidForm
                                                    });
                                                }}
                                            />
                                        </Col>
                                        <Col sm="9">
                                            {this.inputField(
                                                element,
                                                this.state?.selectedHashType?.value
                                            )}
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        );
                    })}
                </Form.Group>
                {this.renderTagField()}
                {!this.props?.initialData && (
                    <FileUploader
                        multiple={true}
                        isDragActive={true}
                        files={this.state.fileToUpload}
                        handleFileUploaded={this.handleFiles}
                        handleRemoveFile={this.handleRemoveFile}
                        showTooltip={this.state.editDuplicatedFiles}
                        className={
                            this.state.editDuplicatedFiles ? 'disabled' : '' // << null
                        }
                    />
                )}

                {this.renderEditor()}
                <div className="float-start mt-4 mb-4">
                    <div className="mui-ms-2-5">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={this.state.autoLock}
                                    onChange={event => {
                                        this.setState({
                                            autoLock: event.target.checked
                                        });
                                    }}
                                    slotProps={{ input: { id: 'switch-auto-lock' } }}
                                />
                            }
                            label={
                                <Translate message="Auto lock on creation" />
                            }
                        />
                    </div>
                </div>
            </>
        );
    };

    renderObservables = () => {
        //FM: render observable form with corresponded field (multiple tag) and add-info
        //FM: put invalid class in case value is present in validForm object with falsy value
        const objTypeValue = this.state.objType.value;
        const tagClassName = [
            this.notValidForm(objTypeValue) ? 'is-invalid' : ''
        ];
        return (
            <>
                <Form.Group controlId="object-name" className="form-group">
                    <Form.Label required={true} className="mb-1">
                        {this.state.objType.label}
                    </Form.Label>
                    <Tag
                        isDisabled={!!this.props.initialData}
                        value={this.state.objValue}
                        className={tagClassName}
                        onChange={tags => {
                            const currentObjTypeValue =
                                this.state.objType.value;
                            //FM: split tag for comma or tab
                            const reformattedTags = this.splitTags(tags);
                            //FM: create array to put valid data
                            const validTags = reformattedTags.filter(tag =>
                                this.validateForm(
                                    currentObjTypeValue,
                                    tag.label,
                                    true
                                )
                            );
                            this.setState({
                                objValue: validTags,
                                isValidForm: {
                                    ...this.state.isValidForm,
                                    [currentObjTypeValue]: validTags.length > 0
                                }
                            });
                        }}
                    />
                </Form.Group>
                {this.renderTagField()}
                {this.renderEditor()}
                <div className="float-start mt-4 mb-4">
                    <div className="mui-ms-2-5">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={this.state.autoLock}
                                    onChange={event =>
                                        this.setState({
                                            autoLock: event.target.checked
                                        })
                                    }
                                    slotProps={{ input: { id: 'switch-auto-lock' } }}
                                />
                            }
                            label={
                                <Translate message="Auto lock on creation" />
                            }
                        />
                    </div>
                </div>
            </>
        );
    };

    renderTagField = () => {
        return (
            <Form.Group className="form-group">
                <Form.Label className="text-truncate">
                    <Translate message="Tag" />
                </Form.Label>
                <Tag
                    onChange={tags => {
                        this.setState({
                            objectTagValues: tags
                        });
                    }}
                />
            </Form.Group>
        );
    };

    renderFormDetails = () => {
        const { objType, artifactItems, artifactValidationSchema } = this.state;
        if (keysLen(objType)) {
            switch (objType.value) {
                case 'artifacts':
                    return this.renderFiles();
                case 'incident-artifact':
                    return (
                        <>
                            {this.renderTagField()}
                            <IncidentArtifactCreationTable
                                artifactItems={artifactItems}
                                addArtifactItem={this._addArtifactItem}
                                removeArtifactItem={this._removeArtifactItem}
                                updateArtifactItem={this._updateArtifactItem}
                                validationSchema={artifactValidationSchema}
                            />
                        </>
                    );
                default:
                    return this.renderObservables();
            }
        }
        return null;
    };

    componentDidUpdate(prevProps, prevState) {
        const { artifactItems, fileToUpload } = this.state;
        if (artifactItems !== prevState.artifactItems) {
            const schema = getArtifactsValidationSchema([artifactItems]);
            this.setState(
                {
                    artifactValidationSchema: schema
                },
                this._validateFormArtifacts
            );
        }
        if (fileToUpload !== prevState.fileToUpload && fileToUpload.length) {
            //FM: on file upload put the file name as artifact (FILE) title
            const elem = this.filesForms[0];
            const fileName = fileToUpload[0].name;
            this.validateForm(elem.type, fileName, elem.required);
            const data = this.state.objFileData;
            data[elem.id] = fileName;
            this.setState({
                objFileData: data
            });
        }
    }

    render() {
        const { show, onHide } = this.props;
        const classNameForScroll = keysLen(this.state.objType)
            ? 'overflow-auto'
            : ''; // << null
        return (
            <>
                <Modal
                    centered
                    show={show}
                    onHide={onHide}
                    //dialogClassName="mh-100"
                    id="newObject"
                    size="lg"
                >
                    <HeaderModal
                        icon="register"
                        title={
                            this.state.editDuplicatedFiles ? (
                                <Translate message="Edit Object" />
                            ) : (
                                <Translate message="New Object" />
                            )
                        }
                        onHide={onHide}
                    />
                    <>
                        <Modal.Body>
                            <Container fluid>
                                <Row>
                                    <Col
                                        xs={12}
                                        className={classNameForScroll}
                                        style={{
                                            maxHeight: '73vh'
                                        }}
                                    >
                                        <div className="h-100">
                                            <Form.Group
                                                controlId="object-type"
                                                className="row form-group"
                                            >
                                                <div className="col-12">
                                                    <Form.Label
                                                        sm={12}
                                                        column={false}
                                                        required={true}
                                                        className="text-truncate"
                                                    >
                                                        <Translate message="Object Type" />
                                                    </Form.Label>
                                                    <Select
                                                        isDisabled={
                                                            !!this.props
                                                                .initialData ||
                                                            this.state
                                                                .editDuplicatedFiles
                                                        }
                                                        placeholder={
                                                            <Translate message="Please select" />
                                                        }
                                                        options={this.createObjTypeListData()}
                                                        value={
                                                            this.state.objType
                                                                .label
                                                                ? this.state
                                                                      .objType
                                                                : null
                                                            // github.com/JedWatson/react-select/issues/1043
                                                        }
                                                        onChange={event => {
                                                            this.setState({
                                                                isValidForm: {},
                                                                objFileData: {},
                                                                objType: event,
                                                                objValue: '',
                                                                additionalInfo:
                                                                    ''
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </Form.Group>
                                            {this.renderFormDetails()}
                                        </div>
                                    </Col>
                                </Row>
                            </Container>
                        </Modal.Body>
                        <Modal.Footer>
                            {this.state.editDuplicatedFiles ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className="me-3"
                                    onClick={this.onUpdate}
                                    disabled={this.manageApplyButton()}
                                >
                                    <Translate message="Update" />
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className="me-3"
                                    onClick={this.onApply}
                                    disabled={this.manageApplyButton()}
                                >
                                    <Translate message="Create" />
                                </Button>
                            )}
                        </Modal.Footer>
                    </>
                </Modal>
                {this.state.showPageOverlay && (
                    <PageOverlay
                        show={true}
                        text={<Translate message="Loading..." />}
                    />
                )}
            </>
        );
    }
}

AddObjectModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    handleUpdateData: PropTypes.func,
    incidentId: PropTypes.string,
    id: PropTypes.string,
    initialData: PropTypes.object
};

export default AddObjectModal;
